import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { StarIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { Topic } from "../../types/category";
import { Entry } from "../../types/directory";
import { People as ConPeople, Event, Media, Post, Tool } from "../../types/content";
import Spinner from "../layout/Spinner";
import CardWithImageTopAndFooter from "../layout/CardWithImageTopAndFooter";
import { TagIcon } from "@heroicons/react/24/outline";
import { getConsumptionColour } from "../home/Home";
import SupportUs from "../landing/SupportUs";
import { getRecentlyViewed } from "../../slices/log";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import Button from "../layout/Button";
import { updateProfile } from "../../slices/auth";
import { addAlert } from "../../slices/alert";
import { getCategoryOptions } from "../../slices/category";
import DirectoryEntryCard from "../layout/DirectoryEntryCard";
import PostCard from "../layout/PostCard";
import PeopleCard from "../layout/PeopleCard";
import EventCard from "../layout/EventCard";
import ToolCard from "../layout/ToolCard";
import CardWithImageBgAndBadgeTop from "../layout/CardWithImageBgAndBadgeTop";
import { capitalise } from "../../utils/text";

export default function Account() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getRecentlyViewed());
    dispatch(getCategoryOptions());
  }, []);

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);
  const { options: { data: optionsData, loading: optionsLoading } } = useAppSelector((state) => state.category);
  const { recently_viewed: {
    data: recentlyViewed,
    loading: recentlyViewedLoading,
  } } = useAppSelector((state) => state.log);

  // Component level state
  const [showUpdateAccountModal, setShowUpdateAccountModal] = useState(false);
  // 1) Give empty defaults for initial render:
  const [accountState, setAccountState] = useState({
    first_name: "",
    last_name: "",
    favourite_topics: [] as string[],
  });

  // 2) Use effect to populate once user is loaded:
  useEffect(() => {
    if (user && user.profile?.favourite_topics) {
      setAccountState({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        favourite_topics: user.profile.favourite_topics.map(x => x.id),
      });
    }
  }, [user]);

  // Functions
  async function triggerUpdateAccount(e: any) {
    e.preventDefault();
    if (user === null) return;
    const res: any = await dispatch(updateProfile({
      firstName: accountState.first_name === "" ? user?.first_name : accountState.first_name,
      lastName: accountState.last_name === "" ? user?.last_name : accountState.last_name,
      email: user?.email,
      username: user?.username,
      currentPassword: "",
      updatePassword: "",
      confirmPassword: "",
      location: null,
      favouriteTopics: accountState.favourite_topics,
    }));
    if (res.payload.status === 200) {
      setShowUpdateAccountModal(false);
      dispatch(addAlert("Profile updated!", "mint-green"))
    } else {
      dispatch(addAlert("Failed to update profile", "orange"))
    }
  }

  // Constant variables
  const optionsTopics = optionsData.topic === undefined ? [] : optionsData.topic;

  /**
   * "My library" merges two favourites lists that render through the same card.
   *
   * Each item has to carry which list it came from. The card's favourite button
   * looks the object up in `profile.favourite_posts` vs `profile.favourite_media`
   * by `objectType`, so it cannot be derived from the object afterwards.
   *
   * This used to read `obj.content_type`, which the profile payload does not
   * contain — only the search/list endpoints add that key (see content/views.py,
   * where it is merged onto the serialized object). `objectType` was therefore
   * `undefined` for every item here, no branch in FavouriteButton matched, and the
   * heart rendered unfilled on content the user had in fact favourited. Clicking it
   * then tried to *add* the favourite again with no object_type, which the API
   * rejects with 400.
   */
  const favouritePostsAndMedia: Array<{ objectType: "Post" | "Media"; item: Post | Media }> = [
    ...(user?.profile.favourite_posts || []).map((item: Post) => ({ objectType: "Post" as const, item })),
    ...(user?.profile.favourite_media || []).map((item: Media) => ({ objectType: "Media" as const, item })),
  ];
  const topicsData = user?.profile.favourite_topics || [];
  const collectionData = user?.profile.favourite_collections || [];
  const exploreTopicsAndCollections: Array<{ id: string; type: "Topic" | "Collection"; name: string; image: string | null; slug: null | string }> = [];
  topicsData.forEach((topic: Topic) => exploreTopicsAndCollections.push({
    id: topic.id,
    type: "Topic",
    name: topic.name,
    image: topic.image,
    slug: null
  }));
  collectionData.forEach((collection: any) => exploreTopicsAndCollections.push({
    id: collection.id,
    type: "Collection",
    name: collection.name,
    image: collection.images.length > 0 ? collection.images[0] : null,
    slug: collection.slug
  }));
  exploreTopicsAndCollections.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Template navbarMode="solid">

      {/* Update account modal */}
      <Modal
        show={showUpdateAccountModal}
        setShow={setShowUpdateAccountModal}
        confirmText="Update"
        cancelText="Close"
        confirmHandler={() => { }}
        showActionButtons={false}
        showTriggerButton={false}
      >
        <div className="overflow-auto px-1">
          <div className="text-center">
            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-black dark:text-white">
              My profile
            </Dialog.Title>
            <div className="mt-10 grid grid-cols-1 gap-y-8">

              {/* First name */}
              <TextInput
                id="first-name"
                type="text"
                label=""
                borderClass="border border-black"
                showLabel={false}
                onChange={(e) => {
                  setAccountState({ ...accountState, first_name: e.target.value });
                }}
                placeholder="First name"
                small="Your first name will not be publicly visible"
                value={accountState.first_name}
              />

              {/* Last name */}
              <TextInput
                id="last-name"
                type="text"
                label=""
                borderClass="border border-black"
                showLabel={false}
                onChange={(e) => {
                  setAccountState({ ...accountState, last_name: e.target.value });
                }}
                placeholder="Last name"
                small="Your last name will not be publicly visible"
                value={accountState.last_name}
              />

              {/* Username */}
              <TextInput
                id="username"
                type="text"
                label=""
                borderClass="border border-black"
                showLabel={false}
                onChange={(e) => { }}
                placeholder="Username"
                value={user?.username}
                disabled={true}
              />

              {/* Email */}
              <TextInput
                id="email"
                type="email"
                label=""
                borderClass="border border-black"
                showLabel={false}
                onChange={(e) => { }}
                placeholder="Email"
                value={user?.email}
                disabled={true}
              />

              {/* Favourite topics */}
              <h1 className="text-left text-sm sm:text-base font-bold text-dark-grey font-extrabold">Favourite topics</h1>
              <div className="grid grid-cols-1 gap-4">
                {optionsTopics.map((topic: any, idx: number) => {
                  return (
                    <div key={`favourite-topic-${idx}`} className="w-full flex justify-start items-center">
                      <input
                        id="register-marketing"
                        type="checkbox"
                        className="h-4 w-4 text-blue border-gray-300 rounded"
                        checked={accountState.favourite_topics.includes(topic.id)}
                        onChange={(e) => {
                          const existingTopics = accountState.favourite_topics;
                          if (e.target.checked) {
                            existingTopics.push(topic.id);
                          } else {
                            const index = existingTopics.indexOf(topic.id);
                            if (index > -1) {
                              existingTopics.splice(index, 1);
                            }
                          }
                          setAccountState({ ...accountState, favourite_topics: existingTopics });
                        }}
                      />
                      <label htmlFor="register-marketing" className="ml-2 block text-sm md:text-base text-black text-left">
                        {topic.name}
                      </label>
                    </div>
                  )
                })}
              </div>

              {/* Submit button */}
              <Button
                bgColour="dark-blue"
                borderColour="dark-blue"
                onClick={(e) => triggerUpdateAccount(e)}
              >
                <span className="text-white">Continue</span>
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <div className="grid grid-col-1 gap-y-12 lg:gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-20 w-full bg-dark-blue py-12 md:py-20 relative"
          >
            <div className="grid grid-cols-1 gap-y-4 items-center justify-center">
              <h1 className="text-off-white text-3xl md:text-5xl lg:text-6xl font-bold mx-auto">My Account</h1>
            </div>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-20 md:w-36 object-cover -bottom-4 md:-bottom-8 right-16 z-0"
            />
          </div>
        </div>

        <div className="block lg:hidden w-full text-center justify-center items-center">
          <span className="cursor-pointer rounded-full bg-dark-grey px-4 py-0.5 text-white" onClick={() => setShowUpdateAccountModal(true)}>Edit profile</span>
        </div>

        {/* Profile content */}
        <div className="flex justify-center w-full gap-8">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-x-8 w-full lg:w-4/5 mx-8 lg:mx-0">

            {/* Metadata */}
            <div className="invisible lg:visible col-span-3 grid grid-cols-1 h-0 lg:h-fit gap-4 align-start rounded-6xl bg-yellow p-0 lg:p-8">
              <h2 className="font-extrabold text-lg sm:text-xl md:text-2xl text-dark-grey">{`${accountState.first_name} ${accountState?.last_name}`}</h2>
              <span className="px-3 py-0.5 bg-orange shadow-md rounded-full text-white flex justify-center items-center text-sm md:text-base font-bold">Free member</span>
              <span
                onClick={() => setShowUpdateAccountModal(true)}
                className="cursor-pointer px-3 py-0.5 bg-dark-grey shadow-md rounded-full text-white flex justify-center items-center text-sm md:text-base font-bold"
              >
                Edit profile
              </span>

              {/* Favourite topics */}
              {user?.profile.favourite_topics.length !== undefined && user?.profile.favourite_topics.length > 0 && (
                <>
                  <p className="font-normal text-sm sm:text-base">Interested in</p>
                  {user?.profile.favourite_topics.map((topic: Topic, idx) => {
                    return (
                      <span key={idx} className="px-3 py-0.5 bg-transparent rounded-full w-fit text-black border-2 border-black flex justify-center items-center text-xs md:text-sm font-normal">{topic.name}</span>
                    )
                  })}

                </>
              )}

            </div>

            {/* Favourites */}
            <div className="col-span-7 grid grid-cols-1 gap-y-12">
              <>
                <h1 className="text-center text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-dark-grey font-extrabold">Favourites</h1>

                {/* Posts */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">My library</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {/* Guard on the merged list, not on favourite_posts alone: a user
                        with favourited media but no favourited posts used to be told
                        they had favourited nothing, and their media was not rendered. */}
                    {authLoading ? null : favouritePostsAndMedia.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">You haven't favourited any posts yet!</p>
                      </div>
                    ) : (favouritePostsAndMedia.map(({ objectType, item: obj }) => {
                      return (
                        <PostCard
                          key={obj.id}
                          objectId={obj.id}
                          objectType={objectType}
                          image={obj.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : obj.images[0]}
                          bgColour="dark-beige"
                          href={obj.url}
                          title={obj.display_title}
                          authors={obj.authors}
                          primaryTags={obj.display_primary_tags}
                          synopsis={obj.synopsis}
                          consumption={obj.consumption}
                          user={user}
                          rating={obj.rating}
                          reviews={obj.reviews}
                          price={obj.price}
                          locked={obj.locked}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

                {/* Events */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">Events</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {authLoading ? null : user?.profile.favourite_events === undefined ? null : user?.profile.favourite_events.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">You haven't favourited any events yet!</p>
                      </div>
                    ) : (user?.profile.favourite_events.map((obj: Event, idx: number) => {
                      return (
                        <EventCard
                          key={obj.id}
                          objectId={obj.id}
                          objectType="Event"
                          image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                          bgColour="dark-beige"
                          href={obj.url}
                          title={obj.title}
                          host={obj.host}
                          city={obj.city.name}
                          cityDetails={obj.city_details}
                          startDate={obj.start_date}
                          endDate={obj.end_date}
                          synopsis={obj.synopsis}
                          primaryTags={obj.tags}
                          user={user}
                          rating={obj.rating}
                          reviews={obj.reviews}
                          price={obj.price}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

                {/* Directory */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">Directory Listings</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {authLoading ? null : user?.profile.favourite_directory_entries === undefined ? null : user?.profile.favourite_directory_entries.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">You are not following anyone yet</p>
                      </div>
                    ) : (user?.profile.favourite_directory_entries.map((obj: Entry, idx: number) => {
                      return (
                        <DirectoryEntryCard
                          key={obj.id}
                          objectId={obj.id}
                          // "DirectoryEntry", not "Entry": this string is matched
                          // verbatim by FavouriteButton to pick the favourites list,
                          // and by the API to resolve the model. "Entry" matched
                          // neither, so the heart never filled and clicking it was
                          // rejected with 400 "Invalid object_type".
                          objectType="DirectoryEntry"
                          image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                          bgColour="dark-beige"
                          href={obj.url}
                          name={obj.name}
                          synopsis={obj.synopsis}
                          primaryTags={obj.tags}
                          user={user}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

                {/* Topics and collections */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">Topics & Collections</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {authLoading ? null : exploreTopicsAndCollections.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">No topics or collections yet</p>
                      </div>
                    ) : (exploreTopicsAndCollections.map((content, idx) => {
                      return (
                        <CardWithImageBgAndBadgeTop
                          objectId={content.id}
                          objectType={content.type}
                          showFavouriteButton={true}
                          key={`explore-${idx}`}
                          colour={content.type === "Topic" ? "yellow" : "mint-green"}
                          image={content.image === null ? "/img/graphics/hero/landing-hero.png" : content.image}
                          href={content.type === "Topic" ? `/topic/${content.name}` : `/collection/${content.name}`}
                          badgeText={content.type}
                          cardText={content.name}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

                {/* Tools */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">Tools</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {authLoading ? null : user?.profile.favourite_tools === undefined ? null : user?.profile.favourite_tools.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">You haven't favourited any tools yet!</p>
                      </div>
                    ) : (user?.profile.favourite_tools.map((obj: Tool, idx: number) => {
                      return (
                        <ToolCard
                          key={obj.id}
                          objectId={obj.id}
                          objectType="Tool"
                          image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                          bgColour="dark-beige"
                          href={obj.url}
                          name={obj.name}
                          synopsis={obj.synopsis}
                          primaryTags={obj.tags}
                          user={user}
                          rating={obj.rating}
                          reviews={obj.reviews}
                          price={obj.price}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

                {/* People */}
                <div className="grid grid-cols-1 gap-y-4">
                  <h1 className="text-left text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-dark-grey font-extrabold">People</h1>
                  <div className="flex flex-row overflow-x-auto pb-2 gap-4">
                    {authLoading ? null : user?.profile.favourite_people === undefined ? null : user?.profile.favourite_people.length === 0 ? (
                      <div className="w-full flex justify-center py-12">
                        <p className="text-center text-dark-grey">You haven't favourited any people yet!</p>
                      </div>
                    ) : (user?.profile.favourite_people.map((obj: ConPeople, idx: number) => {
                      return (
                        <PeopleCard
                          key={obj.id}
                          objectId={obj.id}
                          objectType="People"
                          image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                          bgColour="dark-beige"
                          href={obj.url}
                          name={obj.name}
                          profession={obj.profession}
                          synopsis={obj.synopsis}
                          primaryTags={obj.tags}
                          user={user}
                          rating={obj.rating}
                          reviews={obj.reviews}
                        />
                      )
                    })
                    )}
                  </div>
                </div>

              </>
            </div>
          </div>
        </div>

      </div>

      {/* Recently viewed */}
      {recentlyViewedLoading ? (
        <div className="w-full flex justify-center pt-12 pb-32">
          <Spinner />
        </div>
      ) : (
        <div className="w-full px-12 grid grid-cols-1 gap-12 pb-32">
          <h1 className="text-center text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-dark-grey font-extrabold">Recently viewed</h1>
          <div className="flex flex-row overflow-x-auto pb-2 gap-x-4 px-12">
            {recentlyViewed.map((obj: any, idx: number) => {
              if (obj.type === "post") {
                return (
                  <PostCard
                    key={`recently-viewed-${idx}`}
                    objectId={obj.id}
                    objectType="Post"
                    image={obj.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : obj.images[0]}
                    bgColour="dark-beige"
                    href={obj.url}
                    title={obj.display_title}
                    authors={obj.authors}
                    primaryTags={obj.display_primary_tags}
                    synopsis={obj.synopsis}
                    consumption={obj.consumption}
                    user={user}
                    rating={obj.rating}
                    reviews={obj.reviews}
                    price={obj.price}
                    locked={obj.locked}
                  />
                )
              }
              if (obj.type === "media") {
                return (
                  <PostCard
                    key={`recently-viewed-${idx}`}
                    objectId={obj.id}
                    objectType="Media"
                    image={obj.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : obj.images[0]}
                    bgColour="dark-beige"
                    href={obj.url}
                    title={obj.display_title}
                    authors={obj.authors}
                    primaryTags={obj.display_primary_tags}
                    synopsis={obj.synopsis}
                    consumption={obj.consumption}
                    user={user}
                    rating={obj.rating}
                    reviews={obj.reviews}
                    price={obj.price}
                    locked={obj.locked}
                  />
                )
              }
              if (obj.type === "people") {
                <PeopleCard
                  key={`recently-viewed-${idx}`}
                  objectId={obj.id}
                  objectType="People"
                  image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                  bgColour="dark-beige"
                  href={obj.url}
                  name={obj.name}
                  profession={obj.profession}
                  synopsis={obj.synopsis}
                  primaryTags={obj.tags}
                  user={user}
                  rating={obj.rating}
                  reviews={obj.reviews}
                />
              }
              if (obj.type === "event") {
                return (
                  <EventCard
                    key={`recently-viewed-${idx}`}
                    objectId={obj.id}
                    objectType="Event"
                    image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                    bgColour="dark-beige"
                    href={obj.url}
                    title={obj.title}
                    host={obj.host}
                    city={obj.city.name}
                    cityDetails={obj.city_details}
                    startDate={obj.start_date}
                    endDate={obj.end_date}
                    synopsis={obj.synopsis}
                    primaryTags={obj.tags}
                    user={user}
                    rating={obj.rating}
                    reviews={obj.reviews}
                    price={obj.price}
                  />
                )
              }
              if (obj.type === "tool") {
                return (
                  <ToolCard
                    key={`recently-viewed-${idx}`}
                    objectId={obj.id}
                    objectType="Tool"
                    image={obj.images.length > 0 ? obj.images[0] : "/img/graphics/hero/landing-hero.png"}
                    bgColour="dark-beige"
                    href={obj.url}
                    name={obj.name}
                    synopsis={obj.synopsis}
                    primaryTags={obj.tags}
                    user={user}
                    rating={obj.rating}
                    reviews={obj.reviews}
                    price={obj.price}
                  />
                )
              }
              if (obj.type === "topic") {
                return (
                  <CardWithImageBgAndBadgeTop
                    objectId={obj.id}
                    objectType={obj.type}
                    showFavouriteButton={false}
                    key={`explore-${obj.id}`}
                    colour="yellow"
                    image={obj.image === null ? "/img/graphics/hero/landing-hero.png" : obj.image}
                    href={obj.type === "Collection" ? `/collection/${obj.name}` : `/topic/${obj.name}`}
                    badgeText={capitalise(obj.type)}
                    cardText={obj.name}
                    newTab={true}
                  />
                )
              }
              if (obj.type === "collection") {
                return (
                  <CardWithImageBgAndBadgeTop
                    objectId={obj.id}
                    objectType={obj.type}
                    showFavouriteButton={false}
                    key={`explore-${obj.id}`}
                    colour="mint-green"
                    image={obj.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : obj.images[0]}
                    href={obj.type === "Collection" ? `/collection/${obj.name}` : `/topic/${obj.name}`}
                    badgeText={capitalise(obj.type)}
                    cardText={obj.name}
                    newTab={true}
                  />
                )
              }
            })}
          </div>
        </div>
      )}

      {/* Support us */}
      <SupportUs />

    </Template>
  );
}